import { OPERATION_LABELS } from "../constants";
import {
  addDays,
  addMinutes,
  isDateType,
  startOfDay,
  startOfMinute,
  toDate,
  getValueText,
} from "./format";

export const HEADER_FILTER_OPERATIONS = ["anyof", "noneof"];

export const isLogicalOperator = value =>
  typeof value === "string" && ["and", "or"].includes(value.toLowerCase());

export function isCondition(expression) {
  return (
    Array.isArray(expression) &&
    expression.length >= 2 &&
    !Array.isArray(expression[0]) &&
    expression[0] !== "!" &&
    !isLogicalOperator(expression[1] ?? "")
  );
}

export const isNegation = expression => Array.isArray(expression) && expression[0] === "!";

export function normalizeCondition(condition) {
  if (condition.length === 2) return [condition[0], "=", condition[1]];

  return [condition[0], String(condition[1]).toLowerCase(), condition[2]];
}

export function isEmptyFilter(expression) {
  return expression == null || (Array.isArray(expression) && expression.length === 0);
}

export function splitAnd(expression) {
  if (isEmptyFilter(expression)) return [];
  if (isCondition(expression) || isNegation(expression)) return [expression];

  const items = [];
  let onlyAnd = true;

  expression.forEach(item => {
    if (isLogicalOperator(item)) {
      if (item.toLowerCase() !== "and") onlyAnd = false;
    } else items.push(item);
  });

  return onlyAnd ? items : null;
}

export function joinWith(items, operator = "and") {
  const filtered = items.filter(item => !isEmptyFilter(item));

  if (filtered.length === 0) return null;
  if (filtered.length === 1) return filtered[0];

  return filtered.reduce(
    (result, item, index) => (index === 0 ? [item] : [...result, operator, item]),
    []
  );
}

export function combineFilters(...filters) {
  return joinWith(filters, "and");
}

export const getColumnSelector = column => column.dataField || column.name;

function conditionSelector(condition) {
  const selector = condition[0];

  return typeof selector === "string" ? selector : null;
}

function isColumnCondition(item, column, headerFilter) {
  if (!isCondition(item)) return false;

  const [selector, operation] = normalizeCondition(item);

  return (
    selector === getColumnSelector(column) &&
    HEADER_FILTER_OPERATIONS.includes(operation) === headerFilter
  );
}

function replaceColumnCondition(filterValue, column, headerFilter, condition) {
  const items = splitAnd(filterValue);

  if (items === null) return combineFilters(filterValue, condition);

  let inserted = false;
  const next = [];

  items.forEach(item => {
    if (isColumnCondition(item, column, headerFilter)) {
      if (condition && !inserted) next.push(condition);
      inserted = true;
    } else next.push(item);
  });

  if (condition && !inserted) next.push(condition);

  return joinWith(next);
}

export function getFilterRowCondition(filterValue, column) {
  const items = splitAnd(filterValue) || [];
  const condition = items.find(item => isColumnCondition(item, column, false));

  if (!condition) return null;

  const [, operation, value] = normalizeCondition(condition);

  return { operation, value };
}

export function setFilterRowCondition(filterValue, column, operation, value) {
  const empty =
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.every(item => item == null || item === ""));
  const condition = empty ? null : [getColumnSelector(column), operation, value];

  return replaceColumnCondition(filterValue, column, false, condition);
}

export function getHeaderFilterCondition(filterValue, column) {
  const items = splitAnd(filterValue) || [];
  const condition = items.find(item => isColumnCondition(item, column, true));

  if (!condition) return null;

  const [, operation, values] = normalizeCondition(condition);

  return { type: operation, values: Array.isArray(values) ? values : [values] };
}

export function setHeaderFilterCondition(filterValue, column, type, values) {
  const condition =
    values && values.length ? [getColumnSelector(column), type || "anyof", values] : null;

  return replaceColumnCondition(filterValue, column, true, condition);
}

export function removeColumnConditions(filterValue, column) {
  const withoutRow = replaceColumnCondition(filterValue, column, false, null);

  return replaceColumnCondition(withoutRow, column, true, null);
}

export function hasColumnFilter(filterValue, column) {
  return !!(
    getFilterRowCondition(filterValue, column) || getHeaderFilterCondition(filterValue, column)
  );
}

function rangeExpression(selector, start, end) {
  if (start && end) return [[selector, ">=", start], "and", [selector, "<", end]];
  if (start) return [selector, ">=", start];
  if (end) return [selector, "<", end];

  return null;
}

function dateRange(column, value) {
  const date = toDate(value);
  if (!date) return null;

  const atMidnight =
    date.getHours() + date.getMinutes() + date.getSeconds() + date.getMilliseconds() === 0;

  if (column.dataType === "datetime" && !atMidnight) {
    const start = startOfMinute(date);

    return [start, addMinutes(start, 1)];
  }

  const start = startOfDay(date);

  return [start, addDays(start, 1)];
}

function dateExpression(column, selector, operation, value) {
  if (operation === "between") {
    const [from, to] = Array.isArray(value) ? value : [value, null];
    const fromRange = dateRange(column, from);
    const toRange = dateRange(column, to);

    return rangeExpression(selector, fromRange?.[0], toRange?.[1]);
  }

  const range = dateRange(column, value);
  if (!range) return [selector, operation, null];

  const [start, end] = range;

  switch (operation) {
    case "=":
      return rangeExpression(selector, start, end);
    case "<>":
      return [[selector, "<", start], "or", [selector, ">=", end]];
    case "<":
      return [selector, "<", start];
    case "<=":
      return [selector, "<", end];
    case ">":
      return [selector, ">=", end];
    case ">=":
      return [selector, ">=", start];
    default:
      return [selector, operation, start];
  }
}

function toNumber(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;

  const number = Number(String(value).replace(",", "."));

  return Number.isNaN(number) ? value : number;
}

export function parseHeaderFilterDate(value) {
  if (value instanceof Date)
    return { start: startOfDay(value), end: addDays(startOfDay(value), 1) };

  const parts = String(value)
    .split("/")
    .map(part => Number(part));

  if (!parts.length || parts.some(part => Number.isNaN(part))) return null;

  const [year, month, day] = parts;

  if (day)
    return { start: new Date(year, month - 1, day), end: new Date(year, month - 1, day + 1) };
  if (month) return { start: new Date(year, month - 1, 1), end: new Date(year, month, 1) };

  return { start: new Date(year, 0, 1), end: new Date(year + 1, 0, 1) };
}

export function headerFilterValueExpression(column, value) {
  const selector = getColumnSelector(column);

  if (Array.isArray(value)) return value;

  if (column.calculateFilterExpression)
    return column.calculateFilterExpression.call(column, value, "=", "headerFilter");

  if (value == null) return [selector, "=", null];

  if (isDateType(column.dataType)) {
    const range = parseHeaderFilterDate(value);

    return range ? rangeExpression(selector, range.start, range.end) : [selector, "=", value];
  }

  return [selector, "=", value];
}

function conditionExpression(condition, column, target) {
  const [selector, operation, value] = normalizeCondition(condition);

  if (!column) {
    if (operation === "anyof")
      return joinWith(
        (value || []).map(item => [selector, "=", item]),
        "or"
      );
    if (operation === "noneof")
      return [
        "!",
        joinWith(
          (value || []).map(item => [selector, "=", item]),
          "or"
        ),
      ];

    return [selector, operation, value];
  }

  if (operation === "anyof" || operation === "noneof") {
    const values = Array.isArray(value) ? value : [value];
    const expression = joinWith(
      values.map(item => headerFilterValueExpression(column, item)),
      "or"
    );

    if (!expression) return null;

    return operation === "anyof" ? expression : ["!", expression];
  }

  if (column.calculateFilterExpression && !["isblank", "isnotblank"].includes(operation)) {
    const reviveDate = item => (isDateType(column.dataType) ? toDate(item) : item);
    const filterValue = Array.isArray(value) ? value.map(reviveDate) : reviveDate(value);
    const result = column.calculateFilterExpression.call(column, filterValue, operation, target);

    return isEmptyFilter(result) ? null : result;
  }

  const columnSelector = column.dataField || selector;

  if (operation === "isblank")
    return column.dataType === "string"
      ? [[columnSelector, "=", null], "or", [columnSelector, "=", ""]]
      : [columnSelector, "=", null];

  if (operation === "isnotblank")
    return column.dataType === "string"
      ? [[columnSelector, "<>", null], "and", [columnSelector, "<>", ""]]
      : [columnSelector, "<>", null];

  if (isDateType(column.dataType)) return dateExpression(column, columnSelector, operation, value);

  if (column.dataType === "number") {
    if (operation === "between") {
      const [from, to] = Array.isArray(value) ? value : [value, null];
      const fromNumber = toNumber(from);
      const toNumberValue = toNumber(to);

      if (fromNumber != null && toNumberValue != null)
        return [[columnSelector, ">=", fromNumber], "and", [columnSelector, "<=", toNumberValue]];
      if (fromNumber != null) return [columnSelector, ">=", fromNumber];
      if (toNumberValue != null) return [columnSelector, "<=", toNumberValue];

      return null;
    }

    return [columnSelector, operation, toNumber(value)];
  }

  if (column.dataType === "boolean" && typeof value === "string")
    return [columnSelector, operation, value === "true"];

  return [columnSelector, operation, value];
}

export function buildDataSourceFilter(filterValue, columnsBySelector, target = "filterBuilder") {
  if (isEmptyFilter(filterValue)) return null;

  if (isCondition(filterValue)) {
    const selector = conditionSelector(filterValue);

    return conditionExpression(filterValue, selector ? columnsBySelector[selector] : null, target);
  }

  if (isNegation(filterValue)) {
    const inner = buildDataSourceFilter(filterValue[1], columnsBySelector, target);

    return inner ? ["!", inner] : null;
  }

  const result = [];
  let pendingOperator = null;

  filterValue.forEach(item => {
    if (isLogicalOperator(item)) {
      pendingOperator = item.toLowerCase();
      return;
    }

    const expression = buildDataSourceFilter(item, columnsBySelector, target);

    if (!expression) return;

    if (result.length) result.push(pendingOperator || "and");
    result.push(expression);
    pendingOperator = null;
  });

  if (result.length === 0) return null;

  return result.length === 1 ? result[0] : result;
}

function pad(value, length) {
  return String(value).padStart(length, "0");
}

export function serializeDate(date) {
  const parts = [date.getMonth() + 1, "/", date.getDate(), "/", date.getFullYear()];
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const milliseconds = date.getMilliseconds();

  if (hours + minutes + seconds + milliseconds > 0)
    parts.push(
      " ",
      pad(hours, 2),
      ":",
      pad(minutes, 2),
      ":",
      pad(seconds, 2),
      ".",
      pad(milliseconds, 3)
    );

  return parts.join("");
}

export function serializeFilterDates(expression) {
  if (!Array.isArray(expression)) return expression;

  return expression.map(item => {
    if (Array.isArray(item)) return serializeFilterDates(item);
    if (item instanceof Date) return serializeDate(item);

    return item;
  });
}

function quote(text) {
  return `'${text}'`;
}

function operandText(column, value, locale, t) {
  if (value == null || value === "") return t("gridBlank");

  if (column) {
    if (isDateType(column.dataType)) {
      const date = toDate(value);
      const atMidnight =
        date &&
        date.getHours() + date.getMinutes() + date.getSeconds() + date.getMilliseconds() === 0;
      const dateColumn =
        atMidnight && typeof column.format === "string"
          ? { ...column, format: column.format.replace(/[\s,T]*[Hhkm].*$/, "") || undefined }
          : column;

      return getValueText(dateColumn, date || value, locale, "filterPanel");
    }

    return getValueText(column, value, locale, "filterPanel");
  }

  return String(value);
}

export function headerFilterValueText(column, value, locale, t) {
  if (value == null) return t("gridBlank");

  if (Array.isArray(column?.headerFilterDataSource)) {
    const item = column.headerFilterDataSource.find(entry => entry.value === value);

    if (item) return item.text;
  }

  if (column && isDateType(column.dataType) && !(value instanceof Date)) return String(value);

  return operandText(column, value, locale, t);
}

export function conditionToText(condition, columnsBySelector, locale, t) {
  const [selector, operation, value] = normalizeCondition(condition);
  const column = typeof selector === "string" ? columnsBySelector[selector] : null;
  const caption = column?.caption || selector;
  const operationText = t(OPERATION_LABELS[operation] || operation);

  if (operation === "isblank" || operation === "isnotblank")
    return { caption, operation: operationText, value: "" };

  if (operation === "anyof" || operation === "noneof") {
    const values = (Array.isArray(value) ? value : [value]).map(item =>
      quote(headerFilterValueText(column, item, locale, t))
    );

    return { caption, operation: operationText, value: `(${values.join(", ")})` };
  }

  if (operation === "between") {
    const [from, to] = Array.isArray(value) ? value : [value, null];

    return {
      caption,
      operation: operationText,
      value: `${quote(operandText(column, from, locale, t))} ${t("gridAnd").toLowerCase()} ${quote(
        operandText(column, to, locale, t)
      )}`,
    };
  }

  return { caption, operation: operationText, value: quote(operandText(column, value, locale, t)) };
}

export function filterToText(expression, columnsBySelector, locale, t) {
  if (isEmptyFilter(expression)) return "";

  if (isCondition(expression)) {
    const { caption, operation, value } = conditionToText(expression, columnsBySelector, locale, t);

    return `[${caption}] ${operation}${value ? ` ${value}` : ""}`;
  }

  if (isNegation(expression))
    return `${t("gridNot")} (${filterToText(expression[1], columnsBySelector, locale, t)})`;

  const parts = [];
  let expectOperand = true;

  expression.forEach(item => {
    if (isLogicalOperator(item)) {
      parts.push(item.toLowerCase() === "or" ? t("gridOr") : t("gridAnd"));
      expectOperand = true;
      return;
    }

    if (!expectOperand) parts.push(t("gridAnd"));

    const text = filterToText(item, columnsBySelector, locale, t);

    parts.push(isCondition(item) ? text : `(${text})`);
    expectOperand = false;
  });

  return parts.join(" ");
}

export function cloneFilter(expression) {
  if (!Array.isArray(expression)) return expression;

  return expression.map(item => {
    if (Array.isArray(item)) return cloneFilter(item);
    if (item instanceof Date) return new Date(item.getTime());

    return item;
  });
}
